import { HistoryOutlined, LinkOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { Button, message, Table, Tag } from 'antd';
import React, { Fragment, useMemo, useState } from 'react';
import styled from 'styled-components';

import { fetchAPI } from '../../../utils/api';
import { format2dp } from '../../../utils/format';
import SPPO from '../../SupplierPayments/SupplierPaymentPopOver';
import OrderItemAuditHistory from './OrderItemAuditHistory';
import OrderItemDeliveryDate from './OrderItemDeliveryDate';
import { OrderItemV2 } from './types';

const OrderItemV2Table = (props: { visible: boolean; orderId: number }) => {
  const [loading, setLoading] = useState(false);
  const [historyItem, setHistoryItem] = useState<OrderItemV2 | null>(null);

  const orderItems = useQuery<{
    items: OrderItemV2[];
    loading: boolean;
  }>(
    ['order-items-v2', props.orderId],
    async () => {
      if (!props.visible) {
        return { items: [], loading: true };
      }

      try {
        const resp = await fetchAPI(`order/${props.orderId}/items`);

        return { items: resp, loading: false };
      } catch (error) {
        message.error('Failed get order items');
        return { items: [], loading: false };
      }
    },
    {
      staleTime: 5 * 60 * 1000,
      placeholderData: {
        items: [],
        loading: true,
      },
    }
  );
  const tableLoading =
    orderItems.isLoading || orderItems.data?.loading || loading;

  // Collect all SPIDs across order items for bulk status fetch
  const { data: orderItemsData } = orderItems;
  const allSpids = useMemo(() => {
    const items = orderItemsData?.items || [];
    const spids: number[] = [];
    const seen = new Set<number>();
    items.forEach(item =>
      (item.spids || []).forEach(spid => {
        if (!seen.has(spid)) {
          seen.add(spid);
          spids.push(spid);
        }
      })
    );
    return spids;
  }, [orderItemsData]);

  const spidStatuses = useQuery<
    Array<{
      id: number;
      status: string;
      problem_line_items?: Array<{ order_item_id: number; status: string }>;
    }>
  >(
    ['spid-statuses', allSpids.sort()],
    async () => {
      if (!allSpids.length) return [];
      try {
        const resp = await fetchAPI('supplier-payments/statuses', {
          method: 'POST',
          payload: { spids: allSpids },
        });

        return resp || [];
      } catch (error) {
        return [];
      }
    },
    {
      staleTime: 5 * 60 * 1000,
      enabled: allSpids.length > 0,
      placeholderData: [],
    }
  );

  const spidStatusMap = useMemo(() => {
    const map: Record<number, string> = {};
    (spidStatuses.data || []).forEach(item => {
      map[item.id] = item.status;
    });
    return map;
  }, [spidStatuses.data]);

  const spidProblemStatusMap = useMemo(() => {
    const map: Record<number, Record<number, string>> = {};
    (spidStatuses.data || []).forEach(item => {
      const inner: Record<number, string> = {};
      (item.problem_line_items || []).forEach(pl => {
        inner[pl.order_item_id] = pl.status;
      });
      map[item.id] = inner;
    });
    return map;
  }, [spidStatuses.data]);

  return (
    <Fragment>
      <Table
        dataSource={orderItems.data.items}
        scroll={{
          x: true,
        }}
        columns={[
          {
            title: 'Name',
            key: 'name',
            width: 200,
            render: (text, record) => (
              <OrderItemNameCol>
                <b>{record.name}</b>
                {record?.note && (
                  <span
                    style={{
                      display: 'block',
                      marginTop: '5px',
                      fontSize: '12px',
                    }}
                  >
                    <b>Note : </b>
                    {record?.note}
                  </span>
                )}
                {record?.category_item_name && (
                  <span
                    style={{
                      display: 'block',
                      marginTop: '5px',
                      fontSize: '12px',
                    }}
                  >
                    Category : <b>{record?.category_item_name}</b>
                  </span>
                )}
                {record?.supplier && (
                  <span
                    style={{
                      display: 'block',
                      marginTop: '5px',
                      fontSize: '12px',
                    }}
                  >
                    Supplier :{' '}
                    <b>
                      [{record?.supplier_id}] {record?.supplier}
                    </b>
                  </span>
                )}
                {record?.billing_party && (
                  <span
                    style={{
                      display: 'block',
                      marginTop: '5px',
                      fontSize: '12px',
                    }}
                  >
                    Billing Party :{' '}
                    <b>
                      [{record?.billing_party_id}] {record?.billing_party}
                    </b>
                  </span>
                )}
                <div
                  style={{
                    marginTop: '5px',
                    fontSize: '12px',
                  }}
                >
                  <Tag>{record?.paid_by}</Tag>
                </div>
                {record?.external_url && (
                  <span className="external-url">
                    External URL : <b>{record?.external_url}</b>
                  </span>
                )}
                {record?.external_id && (
                  <span
                    style={{
                      display: 'block',
                      marginTop: '5px',
                      fontSize: '12px',
                    }}
                  >
                    External ID : <b>{record?.external_id}</b>
                  </span>
                )}
                <OrderItemDeliveryDate
                  record={record}
                  onSuccess={async silent => {
                    if (!silent) {
                      setLoading(true);
                    }
                    await orderItems.refetch();
                    if (!silent) {
                      setLoading(false);
                    }
                  }}
                />
              </OrderItemNameCol>
            ),
          },
          {
            title: 'History',
            key: 'audit_history',
            width: 60,
            render: (text, record) => (
              <Button
                shape="circle"
                size="small"
                icon={<HistoryOutlined />}
                onClick={() => setHistoryItem(record)}
              />
            ),
          },
          {
            title: 'NCPR',
            key: 'is_ncpr',
            width: 120,
            render: (text, record) => {
              return <>{record.is_ncpr ? <span>Yes</span> : <span>No</span>}</>;
            },
          },
          {
            title: 'Quantity',
            dataIndex: 'quantity',
            width: 50,
            render: v => format2dp(v) || '-',
          },
          {
            title: 'Related SPID(s)',
            render: (text, record) => {
              return (
                <UlWrapper
                  style={{
                    listStyleType: 'None',
                    gap: '6px',
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  {(record.spids || []).map(spid => {
                    return (
                      <SPPO
                        supplierPaymentId={spid}
                        status={spidStatusMap[spid]}
                        lineItemProblemStatus={
                          spidProblemStatusMap[spid]?.[record.id]
                        }
                      />
                    );
                  })}
                </UlWrapper>
              );
            },
            width: 50,
          },
          {
            title: 'Fulfilled Quantity',
            dataIndex: 'fulfilled_qty',
            width: 50,
            render: v => format2dp(v) || '-',
          },
          { title: 'Currency', dataIndex: 'currency', width: 20 },
          {
            title: 'Price',
            dataIndex: 'price',
            width: 50,
            render: v => format2dp(v) || '-',
          },
          {
            title: 'Total',
            dataIndex: 'total',
            width: 50,
            render: v => format2dp(v) || '-',
          },

          {
            title: 'Cost',
            dataIndex: 'cost',
            width: 50,
            render: v => format2dp(v) || '-',
          },
          {
            title: 'Tax %',
            dataIndex: 'tax_rate',
            width: 50,
            render: v => format2dp(v) || '-',
          },
          {
            title: 'Total Cost',
            dataIndex: 'total_cost',
            width: 50,
            render: v => format2dp(v) || '-',
          },

          {
            title: 'Link',
            render: (text, record) => (
              <Button
                shape="circle"
                icon={<LinkOutlined />}
                target="_blank"
                href={record.external_url}
              />
            ),
            width: 50,
          },
        ]}
        loading={tableLoading}
        size="small"
        pagination={false}
      />
      <OrderItemAuditHistory
        visible={!!historyItem}
        item={historyItem}
        onClose={() => setHistoryItem(null)}
      />
    </Fragment>
  );
};

const UlWrapper = styled.ul`
  li:hover {
    color: blue;
  }
`;

const OrderItemNameCol = styled.div`
  max-width: 300px;

  span.external-url {
    display: -webkit-box;
    margintop: 5px;
    fontsize: 12px;
    overflow: hidden;
    text-overflow: ellipsis;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
  }
`;

export default OrderItemV2Table;
